import * as React from 'react';
import validator, {
  Rule,
  RulesMessages,
  ValidationResponse
} from '../utils/validator';
import '../css/input-text.sass';
import Hat, { HatType } from './Hat';
import shouldFlipBG from '../utils/should-flip-bg';
import Tooltip from './Tooltip';

interface TextInputProps {
  type?: 'text' | 'textarea' | 'password' | 'search';
  placeHolder?: string;
  inputMode?:
    | 'text'
    | 'none'
    | 'decimal'
    | 'numeric'
    | 'tel'
    | 'search'
    | 'email'
    | 'url';
  name: string;
  value?: string;
  iconPath?: string;
  id: string;
  label: string;
  required?: boolean;
  focus?: boolean;
  hasError?: boolean;
  errors?: any;
  maxLength?: number;
  minLength?: number;
  theme?: 'night' | 'day';
  validationRules?: Rule[];
  rulesMessages?: RulesMessages;
  allowRegex?: any;
  onChange?: Function;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  onKeyDown?: Function;
  onEsc?: Function;
  onEnter?: Function;
  onFocus?: Function;
  onLeft?: Function;
  onRight?: Function;
  onUp?: Function;
  onDown?: Function;
  onSpace?: Function;
  children?: any;
  hideLabel?: Boolean;
  readOnly?: boolean;
  hat?: HatType[];
  disabled?: boolean;
  toolTip?: React.ReactNode;
  autocomplete?: string;
  hideSearchIcon?: boolean;
  floatingSearch?: boolean;
  floatingSearchClass?: string;
}
interface TextInputState {
  inputValue?: string;
  complete: boolean;
  validationResponse?: ValidationResponse;
  dateFormat?: string;
  flipBg: string;
}

export default function TextInput(props: TextInputProps) {
  const style: any = {};
  const {
    theme = 'day',
    autocomplete = undefined,
    floatingSearchClass = ''
  } = props;
  const iconStyle: any = {};
  if (props.iconPath) {
    iconStyle.paddingLeft = '24px';
    iconStyle.backgroundImage = `url(${props.iconPath})`;
    style.paddingLeft = '8px';
  }

  const inputElParent: any = React.useRef(null);

  const inputEl: any = React.useRef(null);

  React.useEffect(() => {
    if (props.focus && inputEl && inputEl.current) {
      inputEl.current.focus();
    }
  }, [props.focus]);

  let componentPropsRules: Rule[] = [];

  if (
    props.required &&
    (!props.validationRules ||
      !props.validationRules.some((rule) => rule.ruleName === 'required'))
  ) {
    componentPropsRules.push({
      ruleName: 'required',
      message: props.rulesMessages && props.rulesMessages.required,
      defaultMessage: `${props.label} is required.`
    });
  }

  if (
    props.minLength !== undefined &&
    (!props.validationRules ||
      !props.validationRules.some((rule) => rule.ruleName === 'minLength'))
  ) {
    const minLengthRule: Rule = {
      ruleName: 'minLength',
      ruleValue: props.minLength,
      message: props.rulesMessages && props.rulesMessages.minLength,
      defaultMessage: `${props.label} should have at least ${props.minLength} characters.`
    };
    componentPropsRules.push(minLengthRule);
  }
  if (props.validationRules) {
    componentPropsRules.push(...props.validationRules);
  }

  if (
    props.required ||
    componentPropsRules.some((rule) => rule.ruleName === 'required')
  ) {
    componentPropsRules = componentPropsRules.map((rule: Rule) => {
      if (rule.ruleName !== 'required') {
        if (!rule.dependsOn) {
          return { ...rule, dependsOn: ['required'] };
        }
        rule.dependsOn.push('required');
        return rule;
      }
      return rule;
    });
  }

  const componentValidator = {
    validationRules: [
      {
        fieldName: 'inputValue',
        rules: componentPropsRules
      }
    ]
  };

  const [state, setState] = React.useState<TextInputState>({
    inputValue: props.value || '',
    flipBg: '',
    dateFormat: props.placeHolder || '',
    complete:
      !!props.value &&
      validator.validateComponent(
        { state: { inputValue: props.value } },
        componentValidator,
        null,
        true
      )
  });

  const componentState = { state, setState };

  React.useEffect(() => {
    if (
      !!state.inputValue &&
      validator.validateComponent(
        componentState,
        componentValidator,
        null,
        true
      )
    ) {
      if (props.onComplete !== undefined) {
        props.onComplete(state.inputValue);
      }
    } else if (
      (!!state.inputValue || props.required) &&
      props.onError !== undefined
    ) {
      props.onError(validator.getErrors(componentState));
    }
  }, [state.inputValue]);

  React.useEffect(() => {
    if (state.inputValue !== props.value) {
      const validate = validator.validateComponent(
        { state: { inputValue: props.value } },
        componentValidator,
        null,
        true
      );
      setState({
        ...state,
        inputValue: props.value || '',
        dateFormat: props.placeHolder,
        validationResponse: validate ? undefined : state.validationResponse,
        complete: !!props.value && validate
      });
    }
  }, [props.value]);

  React.useEffect(() => {
    if (state.flipBg === '' && shouldFlipBG(inputElParent, theme)) {
      setState({
        ...state,
        flipBg: ' flipbg'
      });
    }
  }, []);

  return (
    <>
      {props.hat &&
        props.hat.length > 0 &&
        props.hat[0] &&
        props.hat[0].message && <Hat hat={props.hat} theme={theme} />}
      <div
        ref={inputElParent}
        role="presentation"
        onBlur={(e: any) => {
          if (
            !e.currentTarget ||
            !e.relatedTarget ||
            !e.currentTarget.contains(e.relatedTarget)
          ) {
            if (
              validator.validateComponent(componentState, componentValidator)
            ) {
              if (props.onComplete !== undefined) {
                props.onComplete(state.inputValue);
              }
            } else if (props.onError !== undefined) {
              props.onError(validator.getErrors(componentState));
            }
            if (props.onBlur !== undefined) {
              props.onBlur(e);
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.keyCode === 13 || e.keyCode === 32) {
            inputEl.current.focus();
          }
        }}
        tabIndex={-1}
        onClick={() => {
          inputEl.current.focus();
        }}
        className={`${!props.floatingSearch ? 'input-field' : ''} text-field ${
          props.readOnly ? 'read-only-text ' : ''
        } ${state.complete ? 'complete-state ' : ''}${
          props.type === 'search' ? 'search-field ' : ''
        }${theme}${
          props.hasError || validator.hasError(componentState, 'inputValue')
            ? ' error'
            : ''
        }${state.flipBg}${props.disabled ? ' disabled ' : ''}`}
      >
        <div className="text">
          {!props.hideLabel && <label htmlFor={props.id}>{props.label}</label>}
          <div className="align-input-text">
            {!!props.iconPath && (
              <span className="icon" aria-label="Logo" style={iconStyle}>
                {' '}
              </span>
            )}
            <input
              type={props.type || 'text'}
              value={state.inputValue}
              defaultValue={state.inputValue}
              id={props.id}
              name={props.name}
              aria-label={props.label}
              ref={inputEl}
              autoComplete={autocomplete}
              className={`${floatingSearchClass.concat('')}${
                props.hasError ||
                validator.hasError(componentState, 'inputValue')
                  ? 'invalid'
                  : ''
              }`}
              aria-invalid={
                props.hasError ||
                validator.hasError(componentState, 'inputValue')
                  ? 'true'
                  : 'false'
              }
              onChange={(e: any) => {
                if (
                  !props.allowRegex ||
                  e.target.value === '' ||
                  props.allowRegex.test(e.target.value)
                ) {
                  const valid = validator.validateComponent(
                    { state: { inputValue: e.target.value } },
                    componentValidator,
                    null,
                    true
                  );
                  if (props.onChange !== undefined) {
                    props.onChange({
                      ...e,
                      target: {
                        ...e.target,
                        valid
                      }
                    });
                  }
                  setState({
                    ...state,
                    inputValue: e.target.value,
                    dateFormat: e.target.placeHolder,
                    validationResponse: valid
                      ? undefined
                      : state.validationResponse
                  });
                } else {
                  setState({
                    ...state,
                    inputValue: state.inputValue
                  });
                }
              }}
              onKeyDown={(e: any) => {
                if (e.keyCode === 27) {
                  if (props.onEsc !== undefined) {
                    props.onEsc(e.target.value);
                  }
                }
                if (e.keyCode === 13) {
                  if (props.onEnter !== undefined) {
                    props.onEnter(e.target.value);
                  }
                }
                if (e.keyCode === 37) {
                  if (props.onLeft !== undefined) {
                    props.onLeft(e.target.value);
                  }
                }
                if (e.keyCode === 39) {
                  if (props.onRight !== undefined) {
                    props.onRight(e.target.value);
                  }
                }
                if (e.keyCode === 38) {
                  if (props.onUp !== undefined) {
                    props.onUp(e.target.value);
                  }
                }
                if (e.keyCode === 40) {
                  if (props.onDown !== undefined) {
                    props.onDown(e.target.value);
                  }
                }
                if (e.keyCode === 32) {
                  if (props.onSpace !== undefined) {
                    props.onSpace(e.target.value);
                  }
                }
                if (props.onKeyDown !== undefined) {
                  props.onKeyDown(e);
                }
              }}
              inputMode={props.inputMode || 'text'}
              placeholder={props.placeHolder || ' '}
              onFocus={(e: any) => {
                if (props.onFocus !== undefined) {
                  props.onFocus(e);
                }
              }}
              maxLength={props.maxLength}
              style={style}
              readOnly={props.readOnly}
              disabled={props.disabled}
            />
            {props.type === 'search' && !props.hideSearchIcon && (
              <div className="search-icon" />
            )}
          </div>
        </div>

        {props.children}
        {!props.readOnly && !props.floatingSearch && (
          <div className="status-bar">&nbsp;</div>
        )}
        {props.toolTip && <Tooltip toolTip={props.toolTip} />}
      </div>
      {!props.readOnly &&
        ((!validator.isAllValid(componentState) &&
          validator.getErrors(componentState)) ||
          (props.hasError && props.errors))}
    </>
  );
}
